import { createRecord, deleteRecord, fetchAllRecords, updateRecord } from '../api/records';
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import RecordListFilters from "../components/RecordListFilters";
import { IoClose } from "react-icons/io5"
import { fetchAllUsers } from '../api/users';

const Records = () => {
    const queryClient = useQueryClient();

    // Filter state
    const [search, setSearch] = useState("");
    const [worker, setWorker] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 20;

    
    // Modal / sheet
    const [sheetOpen, setSheetOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [currentRecordId, setRecordId] = useState(null);
    const [formTelegramId, setFormTelegramId] = useState('');
    const [formDate, setFormDate] = useState('');
    const [formHouse, setFormHouse] = useState('');
    const [formPrice, setFormPrice] = useState('');

    
    const { data: users = [] } = useQuery({
        queryKey: ['users'],
        queryFn: fetchAllUsers
    });
    
    // Records Query
    const {data = [], isLoading} = useQuery({
        queryKey: ['records'],
        queryFn: fetchAllRecords
    });
    const filteredRecords = data
        .filter(record => search ? record.house.toLowerCase().includes(search.toLowerCase()) : true)
        .filter(record => worker ? String(record.telegram_id) === String(worker) : true)
        .filter(record => dateFrom ? new Date(record.date) >= new Date(dateFrom) : true)
        .filter(record => dateTo ? new Date(record.date) <= new Date(dateTo) : true)
    const total_pages = Math.ceil(filteredRecords.length / PAGE_SIZE);
    const paginatedRecords = filteredRecords.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    useEffect(() => {
        setPage(1);
    }, [search, worker, dateFrom, dateTo])
    
    // Sheet open helpers
    const handleAddOpen = () => {
        setFormTelegramId('');
        setFormDate('');
        setFormHouse('');
        setFormPrice('');
        setModalMode('add');
        setSheetOpen(true);
    };
    const handleEditOpen = (record) => {
        setRecordId(record.id);
        setFormDate(record.date);
        setFormHouse(record.house);
        setFormPrice(record.price);
        setModalMode('edit');
        setSheetOpen(true);
    };

    const handleSubmit = () => {
        const isAdd = modalMode === 'add';
        const fn = isAdd
        ? createRecord({ telegram_id: parseInt(formTelegramId), date: formDate, house: formHouse, price: parseInt(formPrice) })
        : updateRecord( currentRecordId, {record_id: currentRecordId, date: formDate, house: formHouse, price: parseInt(formPrice) });
        fn.then(() => {
            setSheetOpen(false);
            queryClient.invalidateQueries({ queryKey: ['records'] });
        });
    };

    const handleDelete = (id) => {
        if (!window.confirm('Delete this record?')) return;
        deleteRecord(id).then(() => queryClient.invalidateQueries({ queryKey: ['records'] }));
    };

    return (
        <div className="flex flex-col w-full max-w-md mx-auto px-3 pt-4 pb-24">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-white text-3xl font-bold">Records</h1>
                <button 
                onClick={handleAddOpen}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-secondary-800 text-white text-2xl font-bold">+</button>
            </div>

            {/* Filter */}
            <RecordListFilters onChange={(filters) => {
                setWorker(filters.worker);
                setSearch(filters.search);
                setDateFrom(filters.dateFrom);
                setDateTo(filters.dateTo);
                }}/>
                <p className="text-primary-10 text-xs font-mono mt-2 mb-3">
                    {filteredRecords.length} {filteredRecords.length === 1 ? 'RESULT' : 'RESULTS'}
                </p>
                {/* Records List */}
                {isLoading ? (
                    <p className="text-primary-50 text-sm text-center mt-8">Loading...</p>
                ) : paginatedRecords.length === 0? (
                    <p className="text-primary-50 text-sm text-center mt-8">No records found.</p>
                ) : (
                    <div className="flex flex-col gap-3">
                        {paginatedRecords.map((record) => (
                            <div key={record.id} className="bg-primary-800 border border-primary-50 rounded-2xl p-4">
                                
                                {/* Top row: ID + date */}
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-primary-10 text-xs font-mono">=0{record.id}</span>
                                    <span className="text-white text-sm font-mono">{record.date}</span>
                                </div>
                                
                                {/* Worker Name */}
                                <p className="text-white font-medium mb-2">{record.name}</p>
                                
                                {/* House + Price */}
                                <div className="flex justify-between items-center mb-4">
                                    <p className="text-primary-10 text-sm">House: {record.house}</p>
                                    <p className="text-white text-lg font-semibold">${record.price}</p>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleEditOpen(record)}}
                                        className="flex-1 border border-secondary-800 text-secondary-800 bg-secondary-50 rounded-xl py-2 text-sm font-mono">
                                        EDIT
                                    </button>
                                    <button
                                        onClick={() => 
                                            handleDelete(record.id)}
                                        className="flex-1 border border-danger-800 text-danger-800 bg-danger-50 rounded-xl py-2 text-sm font-mono">
                                        DELETE
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {total_pages > 1 && (
                    <div className='flex items-center justify-between mt-4'>
                        <button
                            onClick={() => setPage(p => Math.max(1, p-1))}
                            disabled={page===1}
                            className='px-3 py-2 text-xs font-mono border border-primary-50 text-primary-50 rounded-xl disabled:opacity-30'>
                                PREV
                        </button>
                            <p className='text-primary-50 text-xs font-mono'>
                                {page} / {total_pages}
                            </p>
                        <button
                            onClick={() => setPage(p => Math.min(total_pages, p+1))}
                            disabled={page===total_pages}
                            className='px-3 py-2 text-xs font-mono border border-primary-50 text-primary-50 rounded-xl disabled:opacity-30'>
                                NEXT
                        </button>
                    </div>
                )

                }

            {/* Add/Edit bottom sheet */}
            {sheetOpen && (
            <div className="fixed inset-0 z-50 bg-black/60" onClick={() => setSheetOpen(false)}>
                <div className="absolute bottom-0 left-0 right-0 bg-background-400 border-t border-primary-50 rounded-t-2xl px-5 pt-3 pb-10 overflow-hidden" 
                onClick={e => e.stopPropagation()}>
                    {/* Drag handle */}
                    <div className="w-9 h-1 bg-primary-600 rounded-full mx-auto mb-5" />
                    
                    {/* Sheet header */}
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-white text-base font-semibold">{modalMode === 'add' ? 'Add New Record' : 'Edit Record'}</h3>
                        <button onClick={() => setSheetOpen(false)}>
                            <IoClose className="text-primary-50 text-xl" />
                        </button>
                    </div>

                    {/* Worker selector - only add */}
                    {modalMode === 'add' && (
                        <div className="mb-4">
                            <label className="block text-xs text-primary-50 font-mono mb-1.5">WORKER</label>
                            <select
                                value={formTelegramId}
                                onChange={e => setFormTelegramId(e.target.value)}
                                className="w-full bg-background-600 text-white border border-primary-50 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-secondary-800
                                placeholder:text-primary-10 focus:ring-1 focus:ring-secondary-800">
                                    <option value="">Select worker</option>
                                    {users.map((u,i) => (
                                        <option value={u.telegram_id} key={i}>{u.name}</option>
                                    ))}
                            </select>
                        </div>
                    )}
                    {/* Date */}
                    <div className="mb-4">
                        <label className="block text-xs text-primary-50 font-mono mb-1.5">DATE</label>
                        <input 
                            type='date'
                            value={formDate}
                            onChange={(e) => setFormDate(e.target.value)}
                            className="w-full bg-background-600 text-white border border-primary-50 rounded-xl px-3 py-3 text-sm focus:outline-none
                            placeholder:text-primary-10 focus:border-secondary-800 focus:ring-1 focus:ring-secondary-800"/>
                    </div>
                    {/* House */}
                    <div className="mb-4">
                        <label className="block text-xs text-primary-50 font-mono mb-1.5">HOUSE</label>
                        <input 
                            type='text' 
                            placeholder="House number"
                            value={formHouse}
                            onChange={(e) => setFormHouse(e.target.value)}
                            className="w-full bg-background-600 text-white border border-primary-50 rounded-xl px-3 py-3 text-sm 
                            placeholder:text-primary-10 focus:outline-none focus:border-secondary-800 focus:ring-1 focus:ring-secondary-800"/>
                    </div>
                    {/* Price */}
                    <div className="mb-4">
                        <label className="block text-xs text-primary-50 font-mono mb-1.5">PRICE</label>
                        <input 
                            type='number'
                            inputMode="decimal"
                            placeholder="0.00"
                            value={formPrice}
                            onChange={(e) => setFormPrice(e.target.value)}
                            className="w-full bg-background-600 text-white border border-primary-50 rounded-xl px-3 py-3 text-sm 
                            placeholder:text-primary-10 focus:outline-none focus:border-secondary-800 focus:ring-1 focus:ring-secondary-800"/>
                    </div>
                    {/* Actions */}
                    <div className="flex gap-3">
                        <button
                            onClick={() => setSheetOpen(false)}
                            className="flex-1 bg-primary-800 text-primary-50 rounded-xl py-3 text-xs font-mono">
                            CANCEL
                        </button>
                        <button
                            onClick={handleSubmit}
                            className="flex-1 bg-secondary-800 text-white rounded-xl py-3 text-xs font-mono font-bold">
                            {modalMode === 'add' ? 'ADD RECORD' : 'SAVE CHANGES'}
                        </button>
                    </div>
                </div>
            </div>
        )}
    </div>
    );
};

export default Records;